/**
 * InsumosApiClient -- HTTP client read-only para catalogo de insumos.
 *
 * Endpoint NexoAPI v1.6.20+ (F2-A / PANEL-COMPRAS-EDICION-AVANZADA-V1-001):
 *   GET /api/v1/businesses/{businessId}/insumos?q=&page=&pageSize=
 *
 * Consumido por panel modal de match manual (T3P + F2-P) cuando el workflow
 * Purchase no encontro candidatos para un item factura - el usuario busca el
 * insumo correcto para hacer match. Backend tiene cache 30s + ILIKE en 3 cols
 * (nombre, nombre_normalizado, grupo). Filtra estado='archivado'.
 *
 * Adapter shape (panel<->API): camelCase API -> snake_case panel para alinear
 * con candidatos[] del item factura (mismas keys producto_existente_id, nombre,
 * presentacion, unidad, similitud sintetica desde el match score).
 *
 * @module infrastructure/api/InsumosApiClient
 */

import { ApiBase } from './ApiBase.js';

const BASE = '/api/v1/businesses';

// Tope que acepta InsumosService antes de resetear a 25 EN SILENCIO
// (`if (pageSize < 1 || pageSize > 100) pageSize = 25;`). Nunca pedir mas que esto.
const INSUMOS_MAX_PAGE_SIZE = 100;

/** Mapea InsumoListItemDto -> shape panel (consistente con candidatos[] del item). */
function adaptInsumo(api) {
  if (!api) return null;
  return {
    producto_existente_id: api.id,
    insumo_id: api.id,
    nombre: api.nombre || '',
    nombre_normalizado: api.nombreNormalizado || '',
    presentacion: api.presentacion || '',
    unidad: api.unidad || '',
    grupo: api.grupo || '',
    costo_referencia: api.costoReferencia || 0,
    inventario: api.inventario || 0,
    estado: api.estado || ''
  };
}

export const InsumosApiClient = {
  /**
   * Busca insumos del catalogo con filtro paginado.
   * @param {string} businessId
   * @param {{ q?:string, page?:number, pageSize?:number }} params
   */
  async searchInsumos(businessId, params = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };

    const qs = new URLSearchParams();
    if (params.q)         qs.set('q',        String(params.q).trim());
    if (params.page)      qs.set('page',     String(params.page));
    if (params.pageSize)  qs.set('pageSize', String(params.pageSize));

    const query = qs.toString();
    const res = await ApiBase.get(`${BASE}/${businessId}/insumos${query ? `?${query}` : ''}`);
    if (!res.ok) return res;

    const apiData = res.data?.data || res.data || {};
    const apiItems = Array.isArray(apiData.items) ? apiData.items : [];
    const items = apiItems.map(adaptInsumo).filter(Boolean);

    return {
      ok: true,
      data: {
        items,
        totalCount: typeof apiData.totalCount === 'number' ? apiData.totalCount : items.length,
        page: apiData.page || 1,
        pageSize: apiData.pageSize || items.length,
        q: apiData.q || params.q || ''
      }
    };
  },

  /**
   * Trae TODOS los insumos del negocio paginando en loop. Usar cuando se necesita
   * el catalogo completo (ej. dropdown de insumos del movimiento manual).
   *
   * CART-1 (bit #2995, auditoria grep pageSize): InsumosService hace
   *   `if (pageSize < 1 || pageSize > 100) pageSize = 25;`
   * => pedir pageSize=500 devolvia 25 insumos EN SILENCIO (mismo anti-patron que
   * el bug del carrito con ProductosService, pero con tope 100 en vez de 200).
   *
   * @param {string} businessId
   * @param {{ q?:string, pageSize?:number, maxPages?:number }} params
   * @returns {Promise<{ok:boolean, data?:{items:Array,totalCount:number,pages:number,partial:boolean}}>}
   */
  async searchAllInsumos(businessId, params = {}) {
    if (!businessId) return { ok: false, error: { code: 'BUSINESS_REQUIRED' } };

    const size = Math.min(Math.max(Number(params.pageSize) || INSUMOS_MAX_PAGE_SIZE, 1), INSUMOS_MAX_PAGE_SIZE);
    const maxPages = Number(params.maxPages) || 25;
    const all = [];
    let page = 1;
    let total = null;
    let partial = false;

    while (page <= maxPages) {
      const res = await InsumosApiClient.searchInsumos(businessId, { q: params.q, page, pageSize: size });
      if (!res.ok) {
        if (page === 1) return res;      // sin nada acumulado -> propagar error
        partial = true; break;           // devolver lo que ya se trajo
      }
      const items = res.data?.items || [];
      all.push(...items);
      total = Number(res.data?.totalCount ?? all.length);

      if (items.length === 0 || items.length < size || all.length >= total) break;
      page++;
      if (page > maxPages) partial = true;
    }

    return { ok: true, data: { items: all, totalCount: total ?? all.length, pages: page, partial } };
  },

  /**
   * OPS-MOV-P1 (bit #3293, contrato #3261 confirmado por Argy-D): searchAllInsumos
   * (catalogo BD canonico, /insumos) NO expone sync_uuid -- InsumoListItemDto no
   * tiene ese campo (verificado leyendo el DTO real en NexoAPI, no un comentario).
   * Pedir que Argy-A lo agregue es la solucion de fondo, pero mientras tanto el
   * kiosk necesita mandar el sync_uuid REAL (no el PK Postgres) para que Desktop
   * matchee el insumo correcto -- sin esto, cada movimiento sigue siendo
   * "fantasma" (evidencia Argy-D: payload 49051421).
   *
   * Enriquecimiento client-side (mismo patron que KIOSK-GRUPOS-FIX/REC-T5): cruza
   * el catalogo BD contra /api/sync/insumos (que SI trae sync_uuid) por nombre
   * normalizado. Devuelve los mismos items de searchAllInsumos + campo
   * `sync_uuid` (null si el insumo no aparece en el sync legacy, ej. creado
   * directo en BD sin pasar por Desktop).
   *
   * @param {string} businessId
   * @returns {Promise<{ok:boolean, data?:{items:Array,totalCount:number,partial:boolean}}>}
   */
  async searchAllInsumosWithSyncUuid(businessId) {
    const [canonRes, syncRes] = await Promise.all([
      InsumosApiClient.searchAllInsumos(businessId),
      ApiBase.get(`/api/sync/insumos?businessId=${encodeURIComponent(businessId)}&page=1&pageSize=500`)
    ]);
    if (!canonRes.ok) return canonRes;

    const syncUuidByName = new Map();
    if (syncRes.ok) {
      const syncItems = syncRes.data?.data?.items || [];
      for (const it of syncItems) {
        const d = it?.data || {};
        const uuid = d.sync_uuid || d.syncUuid || null;
        const nombre = d.Insumo || d.insumo || d.Producto || d.nombre || '';
        if (uuid && nombre) {
          const key = String(nombre).trim().toLowerCase();
          if (!syncUuidByName.has(key)) syncUuidByName.set(key, uuid);
        }
      }
    }

    const items = canonRes.data.items.map(i => ({
      ...i,
      sync_uuid: syncUuidByName.get(String(i.nombre || '').trim().toLowerCase()) || null
    }));

    return { ok: true, data: { ...canonRes.data, items } };
  }
};
